<template>
  <div
    id="eatAttribution"
    class="d-flex justify-center mt-14"
  >
    <div class="article-content">
      <div class="workspace-block">
        <div class="mb-3 d-flex align-start">
          <div class="flex-grow-1">
            <div class="text-caption-primary font-weight-medium">
              {{ $t('article.eat.title') }}
            </div>
            <p class="mb-0 text--secondary caption">
              {{ $t('article.eat.description') }}
            </p>
          </div>
          <LucySpark metric="eeatScore" />
        </div>

        <div
          v-if="loading"
          class="d-flex align-center text--secondary mb-3 caption"
        >
          <v-progress-circular
            indeterminate
            size="16"
            width="2"
            color="primary"
            class="mr-2"
          />
          {{ $t('article.eat.loading') }}
        </div>
        <p
          v-else-if="!hasAuthor"
          class="text--secondary caption mb-3"
        >
          {{ $t('article.eat.empty') }}
        </p>

        <v-row
          dense
          class="eat-fields"
          :class="{ 'eat-fields--locked': fieldsLocked }"
        >
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              :value="value.authorName"
              :label="$t('article.eat.author')"
              :placeholder="$t('article.eat.authorPlaceholder')"
              :disabled="fieldsLocked"
              :readonly="fieldsLocked"
              outlined
              dense
              hide-details="auto"
              @input="patch('authorName', $event)"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              :value="value.authorRole"
              :label="$t('article.eat.role')"
              :placeholder="$t('article.eat.rolePlaceholder')"
              :disabled="fieldsLocked"
              :readonly="fieldsLocked"
              outlined
              dense
              hide-details="auto"
              @input="patch('authorRole', $event)"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              :value="value.datePublished"
              :label="$t('article.eat.published')"
              :placeholder="$t('article.eat.datePlaceholder')"
              :disabled="fieldsLocked"
              :readonly="fieldsLocked"
              outlined
              dense
              hide-details="auto"
              @input="patch('datePublished', $event)"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              :value="value.dateModified"
              :label="$t('article.eat.updated')"
              :placeholder="$t('article.eat.datePlaceholder')"
              :disabled="fieldsLocked"
              :readonly="fieldsLocked"
              outlined
              dense
              hide-details="auto"
              @input="patch('dateModified', $event)"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              :value="value.readMinutes == null ? '' : String(value.readMinutes)"
              :label="$t('article.eat.readMinutes')"
              type="number"
              min="1"
              :disabled="fieldsLocked"
              :readonly="fieldsLocked"
              outlined
              dense
              hide-details="auto"
              @input="patchMinutes($event)"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              :value="value.contributors"
              :label="$t('article.eat.contributors')"
              :placeholder="$t('article.eat.contributorsPlaceholder')"
              :disabled="fieldsLocked"
              :readonly="fieldsLocked"
              outlined
              dense
              hide-details="auto"
              @input="patch('contributors', $event)"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              :value="value.authorBio"
              :label="$t('article.eat.bio')"
              :placeholder="$t('article.eat.bioPlaceholder')"
              :disabled="fieldsLocked"
              :readonly="fieldsLocked"
              outlined
              dense
              rows="3"
              auto-grow
              hide-details="auto"
              @input="patch('authorBio', $event)"
            />
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {EatAttribution, eatFieldsLocked, hasEatAuthor} from '@/service/extractEatAttribution';
    import LucySpark from '@/components/lucy/LucySpark.vue';

    export default Vue.extend({
        name: 'ArticleEatAttribution',
        components: {LucySpark},
        props: {
            loading: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            value(): EatAttribution {
                return this.$store.getters.article.eatAttribution || {
                    authorName: '',
                    authorRole: '',
                    authorBio: '',
                    contributors: '',
                    datePublished: '',
                    dateModified: '',
                    readMinutes: null,
                };
            },
            hasAuthor(): boolean {
                return hasEatAuthor(this.value);
            },
            fieldsLocked(): boolean {
                return eatFieldsLocked(!!this.$store.getters.sharePreview);
            }
        },
        methods: {
            patch(field: keyof EatAttribution, next: string): void {
                this.$store.commit('setArticleEatAttribution', {[field]: next});
            },
            patchMinutes(next: string): void {
                const parsed = Number(next);
                this.$store.commit('setArticleEatAttribution', {
                    readMinutes: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
                });
            }
        }
    });
</script>

<style scoped lang="sass">
#eatAttribution
    pointer-events: auto

.eat-fields
    pointer-events: auto

.eat-fields--locked
    pointer-events: none
</style>
